import { useEffect } from 'react';
import { formatNumber } from '../../../utils/formatters';
import { benefitService } from '../../../services/benefitService';
import { FUND_TYPES } from '../../../constants/constants';

/**
 * Centralized benefit grant calculation logic.
 * Used by Admins for CARES only.
 *
 * Responsibilities:
 * - Decide WHEN a calculation should run
 * - Build the correct request payload
 * - Call benefitService.recalculateGrant
 * - Write calculated values back into form state
 */
export const useBenefitGrantRecalculation = ({
  fundType,
  arrearsLiabilityAmount,
  arrearage,
  priorGrantAmount,
  pledgeAmount,
  setPledgeAmount,
}) => {

  /**
   * Small helper to run a debounced grant calculation.
   * Keeps each useEffect focused on business rules,
   * not timing / async boilerplate.
   */
  const runGrantRecalculation = (buildRequest, handleResponse) => {
    const timeout = setTimeout(async () => {
      try {
        const request = buildRequest();
        if (!request) return;

        const response = await benefitService.recalculateGrant(request);
        if (!response?.success) return;

        handleResponse(response);
      } catch (err) {
        console.error('Grant recalculation failed', err);
      }
    }, 500);

    return () => clearTimeout(timeout);
  };

  /* =========================================================
    CARES (WA only – required arrears liability and arrearage
    ========================================================= */
  useEffect(() => {
    return runGrantRecalculation(
      () => {
        if (fundType !== FUND_TYPES.CARES) return null;
        if (arrearsLiabilityAmount === '' || arrearsLiabilityAmount === null) return null;
        if (!arrearage) return null;

        return {
          arrearsLiabilityAmount: parseFloat(arrearsLiabilityAmount),
          arrearage: parseInt(arrearage),
          priorGrantAmount: priorGrantAmount? parseFloat(priorGrantAmount): 0,
        };
      },
      (response) => {
        // Guarded sets ensure idempotent effects and prevent re-fetch loops
        const normalizedPledgeAmount = formatNumber(response.calculatedPledgeAmount) ?? '';

        if (pledgeAmount !== normalizedPledgeAmount)
          setPledgeAmount(normalizedPledgeAmount);
      }
    );
  }, [
    fundType,
    arrearsLiabilityAmount,
    arrearage,
  ]);
};